import { TableColumnConfig } from '../../components/sql_table/tableMain';
import { ChangelogItem } from '../../contexts/tableDefinitions';

// Deduplicates changelog entries using a composite key, keeping the latest by changed_at.
// Entries with no natural key (both record_id and record_rid empty) are treated as unique.
export function deduplicateChangelog(
    entries: ChangelogItem[],
    getKey: (e: ChangelogItem, idx: number) => string,
): Map<string, ChangelogItem> {
    const map = new Map<string, ChangelogItem>();
    entries.forEach((e, idx) => {
        const key = getKey(e, idx);
        const existing = map.get(key);
        if (!existing || new Date(e.changed_at) > new Date(existing.changed_at)) {
            map.set(key, e);
        }
    });
    return map;
}

// Interfaces 

export interface SubTableConfig {
    tableName: string;
    parentTable: string;
    parentField: string;
}

export interface EditResult {
    table_name: string;
    record_id: string;
    record_rid: string;
    edited_col: string;
    changelog_value: string;
    current_value: string | null;
    changed_at: string;
    changed_by: string;
    status: 'match' | 'mismatch' | 'not_found' | 'unverifiable';
}

export interface DeleteResult {
    table_name: string;
    record_id: string;
    record_rid: string;
    changed_at: string;
    changed_by: string;
    status: 'deleted' | 'not_deleted';
}

export interface CreateResult {
    table_name: string;
    record_id: string;
    record_rid: string;
    changed_at: string;
    changed_by: string;
    status: 'created' | 'not_created' | 'unknown';
}

export interface SplitResults {
    edit: EditResult[];
    delete: DeleteResult[];
    create: CreateResult[];
}

// ─── Pure utility functions

// Resolve a column name against a row object, trying camelCase and snake_case variants.
// colFound=false means the column is absent from the API response.
export function resolveColValue(row: any, colName: string): { colValue: string; colFound: boolean } {
    const snake = colName.replace(/([A-Z])/g, '_$1').toLowerCase();
    const camel = colName.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    for (const key of [colName, snake, camel]) {
        if (key in row) return { colValue: row[key] != null ? String(row[key]) : '', colFound: true };
    }
    return { colValue: '', colFound: false };
}

// Normalize a value for comparison: rounds numbers to 2dp, normalizes datetime strings to timestamps.
export function normalizeForCompare(v: string | null | undefined): string {
    const s = (v ?? '').trim();
    if (s === '') return s;
    const n = Number(s);
    if (!isNaN(n)) return String(Math.round(n * 100) / 100);
    if (/\d{4}-\d{2}-\d{2}/.test(s)) {
        const iso = s
            .replace(' ', 'T')
            .replace(/T(\d{1,2}):(\d{1,2}):(\d{1,2})/, (_, h, m, sec) =>
                `T${h.padStart(2, '0')}:${m.padStart(2, '0')}:${sec.padStart(2, '0')}`
            );
        const d = new Date(iso);
        if (!isNaN(d.getTime())) return String(d.getTime());
    }
    return s;
}

// Look up a value from a JSON sub-table embedded in a parent table row.
export function getValueFromSubTable(
    apiData: any,
    config: SubTableConfig,
    recordId: string,
    editedCol: string,
): { value: string | null; found: boolean } {
    for (const row of (apiData?.[config.parentTable] || [])) {
        try {
            const raw = row[config.parentField];
            const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (Array.isArray(data)) {
                const sub = data.find((r: any) => String(r.id) === String(recordId));
                if (sub) return { value: sub[editedCol] != null ? String(sub[editedCol]) : '', found: true };
            }
        } catch {}
    }
    return { value: null, found: false };
}

// ─── Dropdown options

export const DAYS_OPTIONS = Array.from({ length: 10 }, (_, i) => ({
    value: String(i + 1),
    label: i === 0 ? 'Last 1 day' : `Last ${i + 1} days`,
}));

// ─── Column configs

export const EDIT_COLUMNS: TableColumnConfig[] = [
    { colName: 'status_display', colType: 'text', colHeader: 'Status' },
    { colName: 'table_name', colType: 'text', colHeader: 'Table' },
    { colName: 'record_id', colType: 'text', colHeader: 'Row ID' },
    { colName: 'record_rid', colType: 'text', colHeader: 'RID' },
    { colName: 'edited_col', colType: 'text', colHeader: 'Column' },
    { colName: 'changelog_value', colType: 'text', colHeader: 'Changelog Value' },
    { colName: 'current_value', colType: 'text', colHeader: 'Current DB Value' },
    { colName: 'changed_by', colType: 'text', colHeader: 'Changed By' },
    { colName: 'changed_at', colType: 'datetime', colHeader: 'Changed At' },
];

export const DELETE_COLUMNS: TableColumnConfig[] = [
    { colName: 'status_display', colType: 'text', colHeader: 'Status' },
    { colName: 'table_name', colType: 'text', colHeader: 'Table' },
    { colName: 'record_id', colType: 'text', colHeader: 'Row ID' },
    { colName: 'record_rid', colType: 'text', colHeader: 'RID' },
    { colName: 'changed_by', colType: 'text', colHeader: 'Deleted By' },
    { colName: 'changed_at', colType: 'datetime', colHeader: 'Deleted At' },
];

export const CREATE_COLUMNS: TableColumnConfig[] = [
    { colName: 'status_display', colType: 'text', colHeader: 'Status' },
    { colName: 'table_name', colType: 'text', colHeader: 'Table' },
    { colName: 'record_id', colType: 'text', colHeader: 'Row ID' },
    { colName: 'record_rid', colType: 'text', colHeader: 'RID' },
    { colName: 'changed_by', colType: 'text', colHeader: 'Created By' },
    { colName: 'changed_at', colType: 'datetime', colHeader: 'Created At' },
];

export const HISTORICAL_COLUMNS: TableColumnConfig[] = [
    { colName: 'action', colType: 'text', colHeader: 'Action' },
    { colName: 'table_name', colType: 'text', colHeader: 'Table' },
    { colName: 'edited_col', colType: 'text', colHeader: 'Column' },
    { colName: 'old_value', colType: 'text', colHeader: 'Old Value' },
    { colName: 'new_value', colType: 'text', colHeader: 'New Value' },
    { colName: 'changed_by', colType: 'text', colHeader: 'Changed By' },
    { colName: 'changed_at', colType: 'datetime', colHeader: 'Changed At' },
];

// ─── Row color configs

export const EDIT_ROW_COLOR = [
    { condition: (row: any) => row._status === 'match', color: '#d4edda' },
    { condition: (row: any) => row._status === 'mismatch', color: '#f8d7da' },
    { condition: (row: any) => row._status === 'not_found', color: '#fff3cd' },
    { condition: (row: any) => row._status === 'unverifiable', color: '#e8e8e8' },
];

export const DELETE_ROW_COLOR = [
    { condition: (row: any) => row._status === 'deleted', color: '#d4edda' },
    { condition: (row: any) => row._status === 'not_deleted', color: '#f8d7da' },
];

export const CREATE_ROW_COLOR = [
    { condition: (row: any) => row._status === 'created', color: '#d4edda' },
    { condition: (row: any) => row._status === 'not_created', color: '#f8d7da' },
    { condition: (row: any) => row._status === 'unknown', color: '#e8e8e8' },
];